/**
 * useHealthSync — auto-sync weight/steps from HealthKit/Health Connect on app open.
 *
 * Usage:
 *   const { latestWeightKg, todaySteps, syncWorkout, syncWeight } = useHealthSync({ authToken });
 */
import { useCallback, useEffect, useState } from 'react';
import { HealthSyncService } from '../services/health-sync.service';
import { isNative } from '../utils/platform';
import { API } from '../api/client';

export function useHealthSync({ authToken }) {
  const [data, setData] = useState({
    latestWeightKg: null,
    todaySteps: 0,
    restingHeartRate: null,
    lastSyncAt: null,
    permissionGranted: false,
  });

  const requestAndSync = useCallback(async () => {
    if (!isNative || !authToken) return;
    const granted = await HealthSyncService.requestPermissions();
    if (!granted) return;

    const [latestWeight, todaySteps, restingHeartRate] = await Promise.all([
      HealthSyncService.getLatestWeight(),
      HealthSyncService.getTodaySteps(),
      HealthSyncService.getRestingHeartRate(),
    ]);

    const latestWeightKg = latestWeight?.value ?? null;
    setData({ latestWeightKg, todaySteps, restingHeartRate, lastSyncAt: new Date(), permissionGranted: true });

    // Push the reading to DuoFit. Two things were wrong here: the URL was relative,
    // so in the native build (origin capacitor://localhost) it never reached the
    // backend at all, and the empty catch swallowed that silently along with the
    // fact that the endpoint did not exist.
    if (latestWeightKg) {
      try {
        const res = await fetch(`${API}/measurements/sync-from-health`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ weightKg: latestWeightKg }),
        });
        if (!res.ok) {
          console.warn('Health weight sync rejected:', res.status, await res.text());
        }
      } catch (err) {
        console.warn('Health weight sync failed:', err);
      }
    }
  }, [authToken]);

  useEffect(() => { requestAndSync(); }, [requestAndSync]);

  const syncWorkout = useCallback(async (workout) => {
    if (!data.permissionGranted) return;
    await HealthSyncService.writeWorkout(workout);
  }, [data.permissionGranted]);

  const syncWeight = useCallback(async (kg) => {
    if (!data.permissionGranted) return;
    await HealthSyncService.writeWeight(kg);
  }, [data.permissionGranted]);

  return { ...data, syncWorkout, syncWeight, refresh: requestAndSync };
}
